import {
  chunkMetadata,
  chunkOriginRepo,
  filePatternLabel,
  isMatchAllPattern,
  KnowledgeCardConfig,
  KnowledgeCategory,
  KnowledgeFilterField,
  KnowledgeMetaItem,
  KnowledgeOptionFetcher
} from '~/utils/knowledge/types'

// File pattern: mono glob for a specific pattern; plain "All files" prose for match-all.
const FILE_PATTERN_META: KnowledgeMetaItem = {
  icon: 'filter',
  mono: (c) => !isMatchAllPattern(c.filePattern),
  value: (c) => filePatternLabel(c.filePattern)
}

// Each category renders with its own icon (like issue severity), not a generic tag.
function categoryIcon(category: KnowledgeCategory | null | undefined): string {
  switch (category) {
    case KnowledgeCategory.Security:
      return 'security'
    case KnowledgeCategory.Style:
      return 'style'
    case KnowledgeCategory.Issue:
      return 'flag'
    default:
      return ''
  }
}

const CATEGORY_OPTIONS = [
  { value: KnowledgeCategory.Issue, label: 'Issue', icon: categoryIcon(KnowledgeCategory.Issue) },
  {
    value: KnowledgeCategory.Security,
    label: 'Security',
    icon: categoryIcon(KnowledgeCategory.Security)
  },
  { value: KnowledgeCategory.Style, label: 'Style', icon: categoryIcon(KnowledgeCategory.Style) }
]

const CATEGORY_LABEL: Record<string, string> = CATEGORY_OPTIONS.reduce((acc, opt) => {
  acc[opt.value] = opt.label
  return acc
}, {} as Record<string, string>)

const CATEGORY_META: KnowledgeMetaItem = {
  icon: (c) => categoryIcon(c.category),
  value: (c) => (c.category ? CATEGORY_LABEL[c.category] ?? '' : '')
}

export const SUPPRESSION_CARD: KnowledgeCardConfig = {
  primary: (c) => chunkMetadata(c).issue_code ?? null,
  title: (c) => c.content,
  body: (c) => chunkMetadata(c).reason ?? null,
  meta: [FILE_PATTERN_META]
}

export const SUPPRESSION_FILTERS: Array<KnowledgeFilterField> = [
  { key: 'content', kind: 'search', label: 'Search', placeholder: 'Search suppressions…' }
]

export const ENFORCEMENT_CARD: KnowledgeCardConfig = {
  title: (c) => c.content,
  meta: [CATEGORY_META, FILE_PATTERN_META]
}

export const ENFORCEMENT_FILTERS: Array<KnowledgeFilterField> = [
  { key: 'content', kind: 'search', label: 'Search', placeholder: 'Search conventions…' },
  { key: 'category', kind: 'multi', label: 'Category', icon: 'layers', options: CATEGORY_OPTIONS }
]

export const CONTEXT_CARD: KnowledgeCardConfig = {
  title: (c) => c.content,
  meta: [CATEGORY_META],
  // Context rows group under their source file.
  groupBy: (c) => c.sourceFile || 'Other'
}

export const CONTEXT_FILTERS: Array<KnowledgeFilterField> = [
  { key: 'content', kind: 'search', label: 'Search', placeholder: 'Search context…' },
  { key: 'category', kind: 'multi', label: 'Type', icon: 'layers', options: CATEGORY_OPTIONS }
]

// ── Owner / org Knowledge page (ENG-4582) ───────────────────────────────────
// Owner cards reuse the repo cards but prepend an Origin Repo meta linking back to the
// repo the chunk was promoted from. Built as factories so the link can embed provider/owner.
interface OwnerCtx {
  provider: string
  owner: string
}

// Shown in place of the origin repo when it was deleted (memory kept, repository == null).
export const ORIGIN_REPO_DELETED_LABEL = 'Origin repository deleted'

function originRepoMeta({ provider, owner }: OwnerCtx): KnowledgeMetaItem {
  return {
    icon: 'git-branch',
    value: (c) => chunkOriginRepo(c) ?? ORIGIN_REPO_DELETED_LABEL,
    href: (c) => {
      const repo = chunkOriginRepo(c)
      return repo ? `/${provider}/${owner}/${repo}/knowledge` : undefined
    }
  }
}

export function ownerSuppressionCard(ctx: OwnerCtx): KnowledgeCardConfig {
  return { ...SUPPRESSION_CARD, meta: [originRepoMeta(ctx), ...SUPPRESSION_CARD.meta] }
}

export function ownerEnforcementCard(ctx: OwnerCtx): KnowledgeCardConfig {
  return { ...ENFORCEMENT_CARD, meta: [originRepoMeta(ctx), ...ENFORCEMENT_CARD.meta] }
}

export function ownerContextCard(ctx: OwnerCtx): KnowledgeCardConfig {
  return {
    ...CONTEXT_CARD,
    meta: [originRepoMeta(ctx), ...CONTEXT_CARD.meta],
    groupBy: (c) => chunkOriginRepo(c) ?? ORIGIN_REPO_DELETED_LABEL
  }
}

const ORIGIN_REPO_FILTER: KnowledgeFilterField = {
  key: 'repository',
  kind: 'combobox',
  label: 'Repository',
  icon: 'git-branch',
  placeholder: 'Search repositories…'
}

export const OWNER_SUPPRESSION_FILTERS = [...SUPPRESSION_FILTERS, ORIGIN_REPO_FILTER]
export const OWNER_ENFORCEMENT_FILTERS = [...ENFORCEMENT_FILTERS, ORIGIN_REPO_FILTER]
export const OWNER_CONTEXT_FILTERS = [...CONTEXT_FILTERS, ORIGIN_REPO_FILTER]

// Bind the page's repo-search fn onto the origin-repo combobox field.
export function withRepoFetcher(
  fields: Array<KnowledgeFilterField>,
  fetchOptions: KnowledgeOptionFetcher
): Array<KnowledgeFilterField> {
  return fields.map((f) => (f.key === 'repository' ? { ...f, fetchOptions } : f))
}
